// DebtFilterPanel.js
// Filter panel for filtering clients by debt threshold - supports multiple filters

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Chip,
  InputAdornment,
  Badge,
  IconButton,
  Stack,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// Default empty filter
const createEmptyFilter = () => ({ field: '', threshold: '' });

const DebtFilterPanel = ({
  onApply,
  appliedFilters = [],
  debtFields = [],
  debtFieldsSchema = {},
  storageKey = 'clients-debt-filter',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Local state for editing filters (array of { field, threshold })
  const [editingFilters, setEditingFilters] = useState([createEmptyFilter()]);

  // Count active filters
  const activeFilterCount = appliedFilters.filter(f => f.field && f.threshold).length;

  // Get label for a debt field
  const getFieldLabel = (fieldKey) => {
    if (fieldKey === 'TotalDebt') {
      return debtFieldsSchema.TotalDebt?.label || 'חוב כולל';
    }
    return debtFieldsSchema[fieldKey]?.label || fieldKey;
  };

  // Build field options: TotalDebt first, then other debt fields
  const fieldOptions = React.useMemo(() => {
    const options = [];

    // Add TotalDebt first if it's in the debt fields
    if (debtFields.includes('TotalDebt')) {
      options.push({ value: 'TotalDebt', label: getFieldLabel('TotalDebt') });
    }

    // Add other debt fields
    debtFields.forEach((field) => {
      if (field !== 'TotalDebt') {
        options.push({ value: field, label: getFieldLabel(field) });
      }
    });

    return options;
  }, [debtFields, debtFieldsSchema]);

  // Get available fields - show all fields for each row (allow same field multiple times)
  const getAvailableFields = () => {
    return fieldOptions;
  };

  // Handle toggle - sync ONLY when opening the panel
  const handleToggle = () => {
    if (!isOpen) {
      // Opening panel - sync editing state with applied filters
      if (appliedFilters.length > 0) {
        // Create deep copies to avoid reference issues
        setEditingFilters(appliedFilters.map(f => ({
          field: f.field || '',
          threshold: f.threshold || ''
        })));
      } else {
        setEditingFilters([createEmptyFilter()]);
      }
    }
    setIsOpen(prev => !prev);
  };

  // Update a filter at index
  const handleFilterChange = (index, key, value) => {
    setEditingFilters(prev => {
      const newFilters = [...prev];
      newFilters[index] = { ...newFilters[index], [key]: value };
      return newFilters;
    });
  };

  // Add a new filter row
  const handleAddFilter = () => {
    setEditingFilters(prev => [...prev, createEmptyFilter()]);
  };

  // Remove a filter row
  const handleRemoveFilter = (index) => {
    setEditingFilters(prev => {
      if (prev.length === 1) {
        // If last one, just clear it
        return [createEmptyFilter()];
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  // Apply filters - NO useCallback to ensure we always have latest editingFilters
  const handleApply = () => {
    // Get current editing filters and filter out incomplete ones
    const validFilters = editingFilters.filter(f => f.field && f.threshold);

    // Call parent with valid filters
    onApply(validFilters);

    // Save to sessionStorage
    try {
      if (validFilters.length > 0) {
        sessionStorage.setItem(storageKey, JSON.stringify(validFilters));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch (e) {
      console.error('Failed to save debt filter to sessionStorage', e);
    }

    setIsOpen(false);
  };

  // Handle Enter key to apply filter
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  // Cancel and close
  const handleCancel = () => {
    setIsOpen(false);
  };

  // Reset form fields
  const handleReset = () => {
    setEditingFilters([createEmptyFilter()]);
  };

  // Clear a single applied filter
  const handleClearSingleFilter = (index) => {
    const newFilters = appliedFilters.filter((_, idx) => idx !== index);
    onApply(newFilters);

    try {
      if (newFilters.length > 0) {
        sessionStorage.setItem(storageKey, JSON.stringify(newFilters));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch (e) {
      console.error('Failed to save debt filter to sessionStorage', e);
    }
  };

  // Clear all applied filters
  const handleClearAll = () => {
    onApply([]);
    sessionStorage.removeItem(storageKey);
  };

  // Get display text for a single filter chip
  const getFilterChipLabel = (filter) => {
    if (!filter.field || !filter.threshold) return '';

    const fieldLabel = getFieldLabel(filter.field);
    // Format number without currency, then add ₪ manually for proper positioning
    const formattedNumber = parseFloat(filter.threshold).toLocaleString('he-IL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return `${fieldLabel} >= ₪${formattedNumber}`;
  };

  // Check if we can add more filters (allow up to 10 filters)
  const MAX_FILTERS = 10;
  const canAddMoreFilters = editingFilters.length < MAX_FILTERS && fieldOptions.length > 0;

  return (
    <>
      {/* Toggle Button and Active Filter Chips - inline with other controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Badge badgeContent={activeFilterCount} color="primary" invisible={activeFilterCount === 0}>
          <Button
            variant={isOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={handleToggle}
            size="small"
            sx={{ direction: 'ltr' }}
          >
            סינון חוב
          </Button>
        </Badge>

        {/* Active filter chips with X to remove each */}
        {!isOpen && appliedFilters
          .filter(filter => filter.field && filter.threshold)
          .map((filter, index) => (
            <Chip
              key={`chip-${filter.field}-${index}`}
              label={getFilterChipLabel(filter)}
              onDelete={() => handleClearSingleFilter(appliedFilters.findIndex(f => f.field === filter.field))}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ direction: 'ltr' }}
            />
          ))}
      </Box>

      {/* Collapsible Filter Panel - rendered separately to not affect flex layout */}
      <Collapse in={isOpen} sx={{ position: 'absolute', zIndex: 10, mt: 1 }}>
        <Paper sx={{ p: 2, minWidth: 350 }} elevation={3}>
          <Stack spacing={2}>
            {editingFilters.map((filter, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Field Selection */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>שדה חוב</InputLabel>
                  <Select
                    value={filter.field}
                    onChange={(e) => handleFilterChange(index, 'field', e.target.value)}
                    label="שדה חוב"
                  >
                    <MenuItem value="">בחר שדה</MenuItem>
                    {getAvailableFields().map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Threshold Input */}
                <TextField
                  size="small"
                  label="סף מינימלי"
                  type="number"
                  value={filter.threshold}
                  onChange={(e) => handleFilterChange(index, 'threshold', e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => e.target.select()}
                  sx={{ width: 130 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0,
                    step: 100,
                  }}
                />

                {/* Remove button */}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFilter(index)}
                  color="error"
                  sx={{ ml: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {/* Add another filter button */}
            {canAddMoreFilters && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddFilter}
                sx={{ alignSelf: 'flex-start', direction: 'ltr' }}
              >
                הוסף תנאי
              </Button>
            )}
          </Stack>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              size="small"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              sx={{ direction: 'ltr' }}
            >
              אפס
            </Button>
            <Button size="small" color="inherit" onClick={handleCancel}>
              ביטול
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
            >
              סינון
            </Button>
          </Box>
        </Paper>
      </Collapse>
    </>
  );
};

export default DebtFilterPanel;
